/**
 * Onboarding — wizard guidato per nuovi tenant.
 *
 * Step 1: Profilo azienda (nome, valuta, anno fiscale, linee di servizio)
 * Step 2: Carica i tuoi dati (upload CSV/Excel opzionale)
 * Step 3: Connetti sorgenti API (opzionale)
 * Step 4: Tutto pronto — riepilogo e accesso alla dashboard
 */

import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronRight,
  Database,
  Layers,
  Plug,
  Rocket,
  Upload,
  X,
} from 'lucide-react'
import { clsx } from 'clsx'
import { KoremarginLogo } from '../components/ui/KoremarginLogo'
import { AlertBanner } from '../components/ui/AlertBanner'
import { IndustrySelector } from '../components/ui/IndustrySelector'
import { ConnectorWizard } from '../components/connectors/ConnectorWizard'
import { useUpdateTenant } from '../hooks/useTenant'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { INDUSTRY_MAP, type IndustrySlug } from '../lib/industries'

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface OnboardingData {
  // Step 0
  industry: IndustrySlug | null
  // Step 1
  companyName: string
  currency: string
  fiscalYearStart: number
  serviceLines: string[]
  // Step 2
  uploadJobId: string | null
  uploadFileName: string | null
  // Step 3
  sourcesAdded: { id: string; name: string; source_type: string }[]
}

const CURRENCIES = [
  { value: 'EUR', label: '€ Euro' },
  { value: 'USD', label: '$ Dollaro USA' },
  { value: 'GBP', label: '£ Sterlina' },
  { value: 'CHF', label: 'Fr. Franco Svizzero' },
]

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
]

const DEFAULT_SERVICE_LINES = ['SG', 'LIC', 'DEV', 'RIV', 'PS']

const STEPS = [
  { id: 0, label: 'Settore',   icon: Layers    },
  { id: 1, label: 'Azienda',   icon: Building2 },
  { id: 2, label: 'Dati',      icon: Upload    },
  { id: 3, label: 'Sorgenti',  icon: Plug      },
  { id: 4, label: 'Pronto!',   icon: Rocket    },
]

// ─────────────────────────────────────────────────────────────────────────────
// StepIndicator
// ─────────────────────────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const done    = step.id < current
        const active  = step.id === current
        const Icon    = step.icon
        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={clsx(
                  'w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 border-2',
                  done   && 'bg-[--km-success]/20 border-[--km-success] text-[--km-success]',
                  active && 'bg-[--km-primary]/20 border-[--km-primary] text-[--km-primary] shadow-[0_0_12px_rgba(124,58,237,0.35)]',
                  !done && !active && 'bg-transparent border-[--km-border] text-[--km-text-muted]',
                )}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={clsx(
                'text-[10px] font-medium hidden sm:block',
                active ? 'text-[--km-primary]' : done ? 'text-[--km-success]' : 'text-[--km-text-muted]',
              )}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={clsx(
                'w-10 sm:w-16 h-px mx-1 mb-5 sm:mb-0 transition-colors duration-300',
                step.id < current ? 'bg-[--km-success]/50' : 'bg-[--km-border]',
              )} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 0 — Selezione settore aziendale
// ─────────────────────────────────────────────────────────────────────────────

function Step0({
  data,
  onChange,
}: {
  data: OnboardingData
  onChange: (patch: Partial<OnboardingData>) => void
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-[--km-text-secondary] leading-relaxed">
        Seleziona il settore che meglio descrive la tua attività. Koremargin configurerà
        automaticamente il mapping dei tuoi file e mostrerà i KPI più rilevanti per il tuo business.
        <br />
        <span className="text-[--km-text-muted]">
          Potrai cambiare questa scelta in qualsiasi momento dalle Impostazioni azienda.
        </span>
      </p>

      <IndustrySelector
        value={data.industry}
        onChange={(slug) => onChange({ industry: slug })}
        showOther
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Profilo azienda
// ─────────────────────────────────────────────────────────────────────────────

function Step1({
  data,
  onChange,
  error,
}: {
  data: OnboardingData
  onChange: (patch: Partial<OnboardingData>) => void
  error: string | null
}) {
  const [newLine, setNewLine] = useState('')

  function addLine() {
    const v = newLine.trim()
    if (v && !data.serviceLines.includes(v)) {
      onChange({ serviceLines: [...data.serviceLines, v] })
    }
    setNewLine('')
  }

  function removeLine(line: string) {
    onChange({ serviceLines: data.serviceLines.filter(l => l !== line) })
  }

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-[--km-text-secondary] mb-1.5">
          Nome azienda <span className="text-[--km-error]">*</span>
        </label>
        <input
          type="text"
          value={data.companyName}
          onChange={e => onChange({ companyName: e.target.value })}
          placeholder="es. Acme S.r.l."
          className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-alt] px-3 py-2.5 text-sm text-[--km-text-primary] placeholder:text-[--km-text-muted] focus:outline-none focus:ring-2 focus:ring-[--km-primary]/40 focus:border-[--km-primary]/60 transition-all"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[--km-text-secondary] mb-1.5">
            Valuta
          </label>
          <select
            value={data.currency}
            onChange={e => onChange({ currency: e.target.value })}
            className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-alt] px-3 py-2.5 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]/40 focus:border-[--km-primary]/60 transition-all"
          >
            {CURRENCIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-[--km-text-secondary] mb-1.5">
            Inizio anno fiscale
          </label>
          <select
            value={data.fiscalYearStart}
            onChange={e => onChange({ fiscalYearStart: Number(e.target.value) })}
            className="w-full rounded-lg border border-[--km-border] bg-[--km-surface-alt] px-3 py-2.5 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]/40 focus:border-[--km-primary]/60 transition-all"
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[--km-text-secondary] mb-1.5">
          Linee di servizio / prodotto
        </label>
        <p className="text-xs text-[--km-text-muted] mb-2">
          Categorie in cui raggruppi ricavi e costi (es. Consulenza, Licenze, SaaS).
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {data.serviceLines.map(line => (
            <span
              key={line}
              className="inline-flex items-center gap-1 rounded-full bg-[--km-primary]/10 border border-[--km-primary]/20 px-2.5 py-1 text-xs font-medium text-[--km-primary]"
            >
              {line}
              <button
                type="button"
                onClick={() => removeLine(line)}
                className="ml-0.5 opacity-60 hover:opacity-100 transition-opacity"
                aria-label={`Rimuovi ${line}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={newLine}
            onChange={e => setNewLine(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLine() } }}
            placeholder="Aggiungi linea di servizio…"
            className="flex-1 rounded-lg border border-[--km-border] bg-[--km-surface-alt] px-3 py-2 text-sm text-[--km-text-primary] placeholder:text-[--km-text-muted] focus:outline-none focus:ring-2 focus:ring-[--km-primary]/40 transition-all"
          />
          <button
            type="button"
            onClick={addLine}
            className="rounded-lg border border-[--km-border] bg-[--km-surface-alt] px-3 py-2 text-sm text-[--km-text-secondary] hover:border-[--km-primary]/40 transition-colors"
          >
            Aggiungi
          </button>
        </div>
      </div>

      {error && <AlertBanner severity="critical" message={error} />}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Carica i tuoi dati
// ─────────────────────────────────────────────────────────────────────────────

const ACCEPTED = '.csv,.xlsx,.xls,.json,.pdf'
const MAX_MB   = 50

function Step2({
  data,
  onChange,
}: {
  data: OnboardingData
  onChange: (patch: Partial<OnboardingData>) => void
}) {
  const [uploading, setUploading]   = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadDone, setUploadDone] = useState(!!data.uploadJobId)
  const fileRef = useRef<HTMLInputElement>(null)

  const industryConfig = data.industry ? INDUSTRY_MAP[data.industry] : null

  async function handleFile(file: File) {
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`Il file supera il limite di ${MAX_MB} MB.`)
      return
    }
    setUploadError(null)
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      if (data.industry) form.append('industry', data.industry)
      const { data: authData } = await supabase.auth.getSession()
      const token = authData.session?.access_token
      const apiBase = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
      const res = await fetch(`${apiBase}/api/v1/upload`, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error(`Upload fallito: ${res.status}`)
      const json = await res.json() as { job_id: string }
      onChange({ uploadJobId: json.job_id, uploadFileName: file.name })
      setUploadDone(true)
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Errore durante il caricamento.')
    } finally {
      setUploading(false)
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  return (
    <div className="space-y-4">
      {/* Checklist file per industry */}
      {industryConfig ? (
        <div className="space-y-3">
          <p className="text-sm text-[--km-text-secondary] leading-relaxed">
            Per un'{' '}
            <span className="font-medium text-[--km-text-primary]">
              {industryConfig.emoji} {industryConfig.label}
            </span>
            , questi sono i file che popolano la dashboard CFO di Koremargin.
            Puoi caricarli uno alla volta, iniziando dal più importante.
          </p>

          {/* Core minimo */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[--km-text-muted] mb-2">
              File essenziali per il tuo settore
            </p>
            <div className="rounded-xl border border-[--km-border] bg-[--km-surface] divide-y divide-[--km-border]">
              {industryConfig.core_minimo.map((f) => (
                <div key={f.table} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[--km-text-primary]">{f.label}</p>
                    <p className="text-xs text-[--km-text-muted]">{f.description}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-[--km-text-muted] shrink-0" aria-hidden="true" />
                </div>
              ))}
            </div>
          </div>

          {/* Core esteso */}
          {industryConfig.core_esteso.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[--km-text-muted] mb-2">
                Consigliati per la lettura CFO completa
              </p>
              <div className="rounded-xl border border-[--km-border] bg-[--km-surface] divide-y divide-[--km-border] opacity-70">
                {industryConfig.core_esteso.map((f) => (
                  <div key={f.table} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-5 h-5 rounded-full border border-[--km-border] flex items-center justify-center shrink-0">
                      <div className="w-2 h-2 rounded-full bg-[--km-border]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[--km-text-secondary]">{f.label}</p>
                      <p className="text-xs text-[--km-text-muted]">{f.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-[--km-text-muted]">
            Carica ora il primo file disponibile. Aggiungerai gli altri in seguito dalla
            sezione <span className="text-[--km-text-secondary]">Carica file</span>.
          </p>
        </div>
      ) : (
        <p className="text-sm text-[--km-text-secondary]">
          Carica un file con i tuoi dati storici (ordini, fatture, ore, contratti) per popolare
          subito la dashboard. Puoi saltare questo passaggio e caricare i dati in seguito.
        </p>
      )}

      {uploadDone ? (
        <div className="rounded-xl border border-[--km-success]/30 bg-[--km-success]/5 px-4 py-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[--km-success]/15 flex items-center justify-center shrink-0">
            <Check className="w-4 h-4 text-[--km-success]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[--km-text-primary] truncate">
              {data.uploadFileName}
            </p>
            <p className="text-xs text-[--km-text-muted]">File caricato correttamente</p>
          </div>
          <button
            type="button"
            onClick={() => { onChange({ uploadJobId: null, uploadFileName: null }); setUploadDone(false) }}
            className="ml-auto text-[--km-text-muted] hover:text-[--km-text-secondary] transition-colors"
            aria-label="Rimuovi file"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onDrop={onDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click() }}
          className={clsx(
            'rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all duration-200',
            uploading
              ? 'border-[--km-primary]/40 bg-[--km-primary]/5'
              : 'border-[--km-border] hover:border-[--km-primary]/40 hover:bg-[--km-primary]/3',
          )}
        >
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]) }}
          />
          <Upload className="w-8 h-8 mx-auto mb-3 text-[--km-text-muted]" />
          {uploading ? (
            <p className="text-sm text-[--km-primary]">Caricamento in corso…</p>
          ) : (
            <>
              <p className="text-sm font-medium text-[--km-text-primary] mb-1">
                {industryConfig
                  ? 'Trascina il primo file del tuo settore o clicca per selezionarlo'
                  : 'Trascina un file qui o clicca per selezionarlo'
                }
              </p>
              <p className="text-xs text-[--km-text-muted]">
                CSV, Excel, JSON, PDF · Max {MAX_MB} MB
                {industryConfig && (
                  <> · Il settore <strong>{industryConfig.label}</strong> è pre-selezionato per l'inferenza automatica</>
                )}
              </p>
            </>
          )}
        </div>
      )}

      {uploadError && <AlertBanner severity="critical" message={uploadError} />}

      {!industryConfig && (
        <p className="text-xs text-[--km-text-muted]">
          Puoi caricare più file in seguito dalla sezione{' '}
          <span className="text-[--km-text-secondary]">Carica file</span>.
        </p>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — Connetti sorgenti
// ─────────────────────────────────────────────────────────────────────────────

function Step3({
  data,
  onChange,
}: {
  data: OnboardingData
  onChange: (patch: Partial<OnboardingData>) => void
}) {
  const [showWizard, setShowWizard] = useState(false)

  function handleSourceAdded(source: { id: string; name: string; source_type: string }) {
    onChange({ sourcesAdded: [...data.sourcesAdded, source] })
    setShowWizard(false)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-[--km-text-secondary]">
        Connetti sorgenti dati automatiche (Google Sheets, Stripe, Float) per tenere la
        dashboard sempre aggiornata. Puoi aggiungere le sorgenti anche in seguito.
      </p>

      {data.sourcesAdded.length > 0 && (
        <div className="space-y-2">
          {data.sourcesAdded.map(s => (
            <div
              key={s.id}
              className="flex items-center gap-3 rounded-lg border border-[--km-success]/30 bg-[--km-success]/5 px-3 py-2.5"
            >
              <div className="w-6 h-6 rounded-full bg-[--km-success]/15 flex items-center justify-center shrink-0">
                <Check className="w-3.5 h-3.5 text-[--km-success]" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[--km-text-primary] truncate">{s.name}</p>
                <p className="text-xs text-[--km-text-muted] capitalize">{s.source_type.replace('_', ' ')}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowWizard(true)}
        className="flex items-center gap-2 rounded-lg border border-dashed border-[--km-border] hover:border-[--km-primary]/40 px-4 py-3 text-sm text-[--km-text-secondary] hover:text-[--km-primary] w-full justify-center transition-all duration-200 hover:bg-[--km-primary]/3"
      >
        <Plug className="w-4 h-4" />
        {data.sourcesAdded.length === 0 ? 'Aggiungi sorgente API' : 'Aggiungi un\'altra sorgente'}
      </button>

      <p className="text-xs text-[--km-text-muted]">
        Gestisci le sorgenti in qualsiasi momento dalla sezione{' '}
        <span className="text-[--km-text-secondary]">Sorgenti dati</span>.
      </p>

      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <ConnectorWizard
            onClose={() => setShowWizard(false)}
            onSuccess={handleSourceAdded}
          />
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Riepilogo e attivazione
// ─────────────────────────────────────────────────────────────────────────────

function Step4({ data }: { data: OnboardingData }) {
  const industryLabel = data.industry
    ? `${INDUSTRY_MAP[data.industry]?.emoji ?? ''} ${INDUSTRY_MAP[data.industry]?.label ?? data.industry}`.trim()
    : 'Generico / configurazione manuale'

  const items = [
    {
      label: 'Settore',
      value: industryLabel,
      icon: Layers,
    },
    {
      label: 'Azienda',
      value: data.companyName,
      icon: Building2,
    },
    {
      label: 'Valuta',
      value: `${data.currency} · Anno fiscale da ${MONTHS[data.fiscalYearStart - 1]}`,
      icon: Database,
    },
    {
      label: 'Linee di servizio',
      value: data.serviceLines.join(', ') || '—',
      icon: Database,
    },
    {
      label: 'File caricato',
      value: data.uploadFileName ?? 'Nessuno (da caricare in seguito)',
      icon: Upload,
    },
    {
      label: 'Sorgenti API',
      value: data.sourcesAdded.length > 0
        ? data.sourcesAdded.map(s => s.name).join(', ')
        : 'Nessuna (da aggiungere in seguito)',
      icon: Plug,
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center py-2 gap-2 text-center">
        <div className="w-14 h-14 rounded-full bg-[--km-success]/15 border border-[--km-success]/30 flex items-center justify-center">
          <Rocket className="w-6 h-6 text-[--km-success]" />
        </div>
        <h3 className="text-base font-semibold text-[--km-text-primary]">Configurazione completata</h3>
        <p className="text-sm text-[--km-text-secondary]">
          Ecco il riepilogo di quello che hai impostato.
        </p>
      </div>

      <div className="rounded-xl border border-[--km-border] bg-[--km-surface-alt] divide-y divide-[--km-border]">
        {items.map(item => (
          <div key={item.label} className="flex items-start gap-3 px-4 py-3">
            <item.icon className="w-4 h-4 text-[--km-text-muted] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-[--km-text-muted] mb-0.5">{item.label}</p>
              <p className="text-sm text-[--km-text-primary] break-words">{item.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Onboarding page
// ─────────────────────────────────────────────────────────────────────────────

export function Onboarding() {
  const navigate = useNavigate()
  const updateTenant = useUpdateTenant()
  const queryClient  = useQueryClient()

  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState<string | null>(null)

  const [data, setData] = useState<OnboardingData>({
    industry: null,
    companyName: '',
    currency: 'EUR',
    fiscalYearStart: 1,
    serviceLines: [...DEFAULT_SERVICE_LINES],
    uploadJobId: null,
    uploadFileName: null,
    sourcesAdded: [],
  })

  function patch(p: Partial<OnboardingData>) {
    setData(prev => ({ ...prev, ...p }))
  }

  function validateStep1(): string | null {
    if (!data.companyName.trim()) return 'Il nome azienda è obbligatorio.'
    if (data.companyName.trim().length < 2) return 'Il nome azienda deve contenere almeno 2 caratteri.'
    return null
  }

  async function next() {
    if (step === 0) {
      // Salva l'industry selezionata (o null) prima di avanzare.
      setSaving(true)
      try {
        await updateTenant.mutateAsync({ settings: { industry: data.industry } })
      } catch {
        // Non bloccare il flusso se il salvataggio dell'industry fallisce.
      } finally {
        setSaving(false)
      }
    }
    if (step === 1) {
      const err = validateStep1()
      if (err) { setError(err); return }
      // Crea il record tenant prima di avanzare allo step 2 (upload).
      // Senza questa chiamata, ingestion_jobs.tenant_id violerebbe il FK → 500.
      setSaving(true)
      try {
        await updateTenant.mutateAsync({
          name: data.companyName.trim(),
          settings: {
            currency: data.currency,
            fiscal_year_start: data.fiscalYearStart,
            service_lines: data.serviceLines,
            onboarding_completed: false,
          },
        })
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Errore nel salvataggio del profilo.')
        setSaving(false)
        return
      } finally {
        setSaving(false)
      }
    }
    setError(null)
    setStep(s => Math.min(s + 1, 4))
  }

  function back() {
    setError(null)
    setStep(s => Math.max(s - 1, 0))
  }

  async function complete() {
    setSaving(true)
    setError(null)
    try {
      await updateTenant.mutateAsync({
        name: data.companyName.trim(),
        settings: {
          industry: data.industry,
          currency: data.currency,
          fiscal_year_start: data.fiscalYearStart,
          service_lines: data.serviceLines,
          onboarding_completed: true,
        },
      })
      // Invalida la query tenant per rileggere onboarding_completed = true
      await queryClient.invalidateQueries({ queryKey: ['tenant'] })
      navigate('/', { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore durante il salvataggio.')
    } finally {
      setSaving(false)
    }
  }

  const stepTitles: Record<number, { title: string; subtitle: string }> = {
    0: { title: 'Il tuo settore',      subtitle: 'Seleziona l\'area di business in cui opera la tua azienda.' },
    1: { title: 'Profilo azienda',     subtitle: 'Come si chiama la tua azienda e come lavori?' },
    2: { title: 'Carica i tuoi dati',  subtitle: 'Importa un file con i dati storici per iniziare.' },
    3: { title: 'Connetti sorgenti',   subtitle: 'Collega sistemi esterni per sincronizzazione automatica.' },
    4: { title: 'Tutto pronto!',       subtitle: 'Controlla il riepilogo e accedi alla dashboard.' },
  }

  const { title, subtitle } = stepTitles[step]

  return (
    <div className="min-h-screen bg-[--km-bg-deep] flex items-center justify-center p-4">
      {/* Decorative orbs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0" aria-hidden="true">
        <div className="absolute -top-32 -right-16 w-[600px] h-[600px] rounded-full opacity-60"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 65%)' }} />
        <div className="absolute -bottom-24 -left-24 w-[500px] h-[500px] rounded-full opacity-50"
          style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.07) 0%, transparent 65%)' }} />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <KoremarginLogo variant="compact" />
        </div>

        {/* Card */}
        <div className="km-panel rounded-2xl border border-[--km-glass-border] bg-[--km-glass] backdrop-blur-xl p-6 sm:p-8 shadow-2xl">
          {/* Step indicator */}
          <StepIndicator current={step} />

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-[--km-text-primary] mb-1">{title}</h1>
            <p className="text-sm text-[--km-text-secondary]">{subtitle}</p>
          </div>

          {/* Step content */}
          <div className="min-h-[260px]">
            {step === 0 && <Step0 data={data} onChange={patch} />}
            {step === 1 && <Step1 data={data} onChange={patch} error={error} />}
            {step === 2 && <Step2 data={data} onChange={patch} />}
            {step === 3 && <Step3 data={data} onChange={patch} />}
            {step === 4 && <Step4 data={data} />}
          </div>

          {/* Error (step 4) */}
          {step === 4 && error && (
            <div className="mt-4">
              <AlertBanner severity="critical" message={error} />
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-5 border-t border-[--km-glass-border]">
            <div>
              {step > 0 && (
                <button
                  type="button"
                  onClick={back}
                  disabled={saving}
                  className="flex items-center gap-1.5 text-sm text-[--km-text-secondary] hover:text-[--km-text-primary] transition-colors disabled:opacity-50"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Indietro
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Salta (step 0, 2 e 3) */}
              {(step === 0 || step === 2 || step === 3) && (
                <button
                  type="button"
                  onClick={next}
                  className="text-sm text-[--km-text-muted] hover:text-[--km-text-secondary] transition-colors"
                >
                  Salta
                </button>
              )}

              {step < 4 ? (
                <button
                  type="button"
                  onClick={next}
                  className="flex items-center gap-1.5 rounded-lg bg-[--km-primary] px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity"
                >
                  Continua
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={complete}
                  disabled={saving}
                  className="flex items-center gap-1.5 rounded-lg bg-[--km-primary] px-5 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {saving ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Salvataggio…
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4" />
                      Vai alla dashboard
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Step counter */}
        <p className="text-center text-xs text-[--km-text-muted] mt-4">
          Passo {step + 1} di {STEPS.length}
        </p>
      </div>
    </div>
  )
}
